import React, { type ReactNode } from 'react';
import { useTheme } from '@/theme';
import { Flex, Text } from '@/components/ui';
import { DateRangePicker, type DateRange, Button } from '@/components/ui';
import i18n from 'i18next';
import type { ErrorMessage as I18nErrorMessage } from '@/utils/errors/types';

interface TabLayoutProps {
  children: ReactNode;
}

export const TabLayout: React.FC<TabLayoutProps> = ({ children }) => {
  const { theme } = useTheme();

  return (
    <Flex direction="column" style={{ flex: 1, minHeight: 0, gap: theme.spacing[5] }}>
      {children}
    </Flex>
  );
};

interface TabHeaderProps {
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  sensorSelect?: ReactNode;
}

export const TabHeader: React.FC<TabHeaderProps> = ({
  dateRange,
  onDateRangeChange,
  sensorSelect,
}) => {
  const { theme } = useTheme();

  return (
    <Flex align="center" justify="between" style={{ width: '100%', gap: theme.spacing[4] }}>
      <DateRangePicker
        value={dateRange}
        onChange={onDateRangeChange}
        size="sm"
        labelSize="xs"
        width="auto"
      />

      {sensorSelect && <div>{sensorSelect}</div>}
    </Flex>
  );
};

interface ErrorMessageProps {
  /** Raw text (legacy) or i18n key pair from `parseDatalogApiError` etc. */
  message: string | I18nErrorMessage;
}

export const ErrorMessage: React.FC<ErrorMessageProps> = ({ message }) => {
  const { theme } = useTheme();

  const text =
    typeof message === 'string'
      ? message
      : [i18n.t(message.title), message.message && message.message !== message.title ? i18n.t(message.message) : null]
          .filter(Boolean)
          .join(' — ');

  return (
    <div
      style={{
        padding: theme.spacing[7],
        backgroundColor: theme.colors.status.danger.stroke.surface,
        borderRadius: theme.borderRadius.md,
        border: `1px solid ${theme.colors.status.danger.stroke.border}`,
      }}
    >
      <Text style={{ color: theme.colors.status.danger.stroke.text }}>{text}</Text>
    </div>
  );
};

interface EmptyStateProps {
  message: string;
}

export const EmptyState: React.FC<EmptyStateProps> = ({ message }) => {
  const { theme } = useTheme();

  return (
    <div
      style={{
        width: '100%',
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing[7],
        backgroundColor: theme.colors.surface.layout.overlay,
        borderRadius: theme.borderRadius.md,
        border: `1px solid ${theme.colors.border.container}`,
        minHeight: '400px',
      }}
    >
      <Text style={{ color: theme.colors.text.secondary, fontSize: theme.fontSize.sm }}>
        {message}
      </Text>
    </div>
  );
};

interface LoadingSkeletonProps {
  count?: number;
  showChart?: boolean;
}

export const LoadingSkeleton: React.FC<LoadingSkeletonProps> = ({
  count = 10,
  showChart = false,
}) => {
  const { theme } = useTheme();

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing[5],
        width: '100%',
      }}
    >
      {showChart && (
        <div
          style={{
            width: '100%',
            backgroundColor: theme.colors.surface.layout.overlay,
            borderRadius: theme.borderRadius.md,
            border: `1px solid ${theme.colors.border.container}`,
            padding: theme.spacing[6],
            height: '400px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Text style={{ fontSize: theme.fontSize.sm, color: theme.colors.text.secondary }}>
            Loading chart...
          </Text>
        </div>
      )}

      <div
        style={{
          width: '100%',
          flex: 1,
          backgroundColor: theme.colors.surface.layout.overlay,
          borderRadius: theme.borderRadius.md,
          border: `1px solid ${theme.colors.border.container}`,
          padding: theme.spacing[6],
          minHeight: '400px',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[4] }}>
          {[...Array(count)].map((_, i) => (
            <div
              key={i}
              style={{
                height: '40px',
                backgroundColor: theme.colors.surface.layout.background,
                borderRadius: theme.borderRadius.sm,
                animation: 'pulse 1.5s ease-in-out infinite',
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

interface DownloadButtonsProps {
  onDownloadPdf?: () => Promise<void>;
  onDownloadExcel?: () => void;
  disabled?: boolean;
}

export const DownloadButtons: React.FC<DownloadButtonsProps> = ({
  onDownloadPdf,
  onDownloadExcel,
  disabled = false,
}) => {
  const { theme } = useTheme();

  if (!onDownloadPdf && !onDownloadExcel) {
    return null;
  }

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: theme.spacing[3],
        marginTop: theme.spacing[3],
      }}
    >
      {onDownloadExcel && (
        <Button
          variant="primary"
          onClick={() => {
            console.log('DownloadButtons: Excel button clicked');
            onDownloadExcel();
          }}
          disabled={disabled}
          style={{ width: '178px' }}
        >
          Download Excel
        </Button>
      )}

      {onDownloadPdf && (
        <Button
          variant="primary"
          onClick={() => {
            console.log('DownloadButtons: PDF button clicked');
            onDownloadPdf();
          }}
          disabled={disabled}
          style={{ width: '178px' }}
        >
          Download PDF
        </Button>
      )}
    </div>
  );
};
